import { type MouseEvent, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  PageBody,
  TableActionsMenu,
  type DataTableColumn,
} from '@wundo/ui';
import { Braces } from 'lucide-react';
import type { ScheduledTask } from '@/types/scheduledTasks.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { TaskDataDialog } from '@/components/admin/scheduled-tasks/TaskDataDialog.tsx';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import { listScheduledTasks } from '@/lib/scheduledTasks.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';

export const Route = createFileRoute('/_app/admin/scheduled-tasks')({
  staticData: { orgScope: 'optional' },
  component: ScheduledTasksPage,
});

/**
 * Everything the schedulers have queued and not yet fired.
 *
 * A read-only queue: nothing on this page changes a task, because the only
 * honest way to cancel one is to stop the scheduler that keeps re-queueing it.
 * Narrowed by the header's organisation and, within it, by one person.
 */
export function ScheduledTasksPage() {
  const [page, setPage] = useState(1);
  const organisationId = useOrganisationFilter()?.id;
  const [user, setUser] = useState<UserOption | null>(null);
  const [viewing, setViewing] = useState<Record<string, unknown> | null>(null);

  /* The people offered are that organisation's; whoever was picked may not be one. */
  useResetOnChange(organisationId, () => {
    setUser(null);
    setPage(1);
  });

  const params = {
    page,
    limit: PAGE_SIZE,
    organisationId,
    userId: user?.id,
  };

  const { data, isPending } = useQuery({
    queryKey: ['scheduledTasks', params],
    queryFn: () => listScheduledTasks(params),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<ScheduledTask>[] = [
    { key: 'scheduler', header: 'Scheduler' },
    {
      key: 'user',
      header: 'User',
      render: (task) =>
        task.user ? (
          <Link
            to='/users/$id'
            params={{ id: task.user.id }}
            className='underline underline-offset-2'
            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
          >
            {task.user.first_name} {task.user.last_name}
          </Link>
        ) : (
          'N/A'
        ),
    },
    {
      key: 'organisation',
      header: 'Organisation',
      render: (task) =>
        task.organisation ? (
          <Link
            to='/organisations/$id'
            params={{ id: task.organisation.id }}
            className='underline underline-offset-2'
            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
          >
            {task.organisation.name}
          </Link>
        ) : (
          'N/A'
        ),
    },
    { key: 'priority', header: 'Priority' },
    {
      key: 'not_before',
      header: 'Not Before',
      render: (task) => formatDateTime(task.not_before),
    },
    {
      key: 'expire_at',
      header: 'Expires At',
      render: (task) => formatDateTime(task.expire_at),
    },
  ];

  /* Narrowing the list starts it at its first page — page 4 of the old one is nothing. */
  function pickUser(next: UserOption | null) {
    setUser(next);
    setPage(1);
  }

  return (
    <>
      <PageHeader title='Scheduled Tasks' />

      <PageBody>
        <DataTable<ScheduledTask>
          label='Scheduled tasks'
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(task) => task.id}
          loading={isPending}
          pagination={{ page, pageSize: PAGE_SIZE, total: data?.meta.totalItems ?? 0 }}
          onPageChange={setPage}
          toolbar={
            <div className='w-56'>
              <UserPicker
                aria-label='User'
                placeholder='User'
                clearable
                organisationId={organisationId}
                value={user}
                onChange={pickUser}
              />
            </div>
          }
          actions={(task) => (
            <TableActionsMenu
              label={`Actions for ${task.scheduler}`}
              items={[
                {
                  label: 'View data',
                  icon: <Braces className='size-4' aria-hidden='true' />,
                  onSelect: () => setViewing(task.data),
                },
              ]}
            />
          )}
          emptyState={<EmptyState title='No scheduled tasks found' />}
        />
      </PageBody>

      <TaskDataDialog
        open={viewing !== null}
        data={viewing}
        onClose={() => setViewing(null)}
      />
    </>
  );
}
